// Ricerca articoli help
import { useSearchParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Search, Book, ArrowLeft } from 'lucide-react'
import { useState, useEffect } from 'react'
import { PageHeader } from '../../components/layout/PageHeader'
import { Input } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { Skeleton } from '../../components/ui/Skeleton'
import api from '../../lib/api'

interface SearchResult {
  id: string
  slug: string
  title: string
  excerpt: string
  category: string
  categoryName: string
}

export function HelpSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [query, setQuery] = useState(searchParams.get('q') ?? '')

  const { data: results, isLoading } = useQuery({
    queryKey: ['help', 'search', query],
    queryFn: async () => {
      try {
        // Il backend usa /help/search?q= che ritorna { query, results, count }
        const res = await api.get<{ results: SearchResult[]; query: string; count: number } | SearchResult[]>('/help/search', {
          params: { q: query },
        })
        const payload = res.data
        if (Array.isArray(payload)) return payload
        return (payload as { results: SearchResult[] }).results ?? []
      } catch {
        return [] as SearchResult[]
      }
    },
    enabled: query.length >= 2,
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (query.trim()) {
      setSearchParams({ q: query })
    }
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <Link to="/help" className="flex items-center gap-1 text-sm text-[var(--fc-primary)] hover:underline">
        <ArrowLeft className="h-4 w-4" />
        Centro assistenza
      </Link>

      <PageHeader title="Cerca articoli" />

      <form onSubmit={handleSearch}>
        <Input
          placeholder="Cerca negli articoli..."
          leftIcon={<Search className="h-4 w-4" />}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </form>

      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="rounded-lg border border-[var(--fc-border)] p-4 space-y-2">
              <Skeleton height={16} className="w-3/4" />
              <Skeleton height={12} className="w-full" />
            </div>
          ))}
        </div>
      )}

      {!isLoading && results !== undefined && (
        <>
          <p className="text-sm text-[var(--fc-foreground-muted)]">
            {results.length} risultati per "{query}"
          </p>

          {results.length === 0 ? (
            <div className="rounded-lg border border-[var(--fc-border)] p-8 text-center">
              <Book className="mx-auto mb-3 h-10 w-10 text-[var(--fc-foreground-subtle)]" aria-hidden="true" />
              <p className="text-sm text-[var(--fc-foreground-muted)]">
                Nessun articolo trovato. Prova a modificare la ricerca.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {results.map((result) => (
                <Link
                  key={result.id}
                  to={`/help/article/${result.slug}`}
                  className="block rounded-lg border border-[var(--fc-border)] bg-[var(--fc-surface)] p-4 hover:border-[var(--fc-primary)] hover:shadow-[var(--fc-shadow)] transition-all"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant="primary">{result.categoryName}</Badge>
                  </div>
                  <h3 className="font-semibold text-[var(--fc-foreground)] hover:text-[var(--fc-primary)]">
                    {result.title}
                  </h3>
                  <p className="mt-1 text-sm text-[var(--fc-foreground-muted)] line-clamp-2">
                    {result.excerpt}
                  </p>
                </Link>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
